// Folding a stream `error` event into the message that was being streamed.
//
// Incident 2026-08-07: an SSE `error` event arriving on a turn that had already
// rendered text and charts replaced the whole answer with the error bubble. The
// rule below is the fix, and it is shared by every surface that turns
// useAgentStream callbacks into message state (ChatPage, useAssistantChat):
//
//   * committed content on screen -> keep it, attach the error as `streamError`
//     (rendered as a notice UNDER the answer by StreamErrorNotice);
//   * nothing committed           -> the message BECOMES the error bubble,
//     exactly as before.
//
// "Committed" is the text_boundary contract's definition: text streamed past
// the last `commit` boundary is provisional narration the pipeline itself would
// have discarded, so it is dropped here too — only committed content is sacred.
import type { MessageType } from "@/pages/ChatPage";

export type StreamErrorInfo = {
  code: string;
  message: string;
  recoverable?: boolean;
};

/**
 * True when the message already shows something the user should keep:
 * committed prose, structured sections, or a rendered visualisation.
 */
export function hasKeepableContent(
  msg: Pick<MessageType, "sections" | "chartData" | "tableData" | "additionalResults" | "closingText">,
  committedText: string,
): boolean {
  return (
    committedText.trim().length > 0 ||
    (msg.closingText?.trim().length ?? 0) > 0 ||
    (msg.sections?.length ?? 0) > 0 ||
    !!msg.chartData ||
    !!msg.tableData ||
    (msg.additionalResults?.length ?? 0) > 0
  );
}

/**
 * Apply a stream error to the streaming message.
 *
 * @param prev          the live message (read inside a functional state update,
 *                      so it includes everything streamed so far)
 * @param committedLen  length of the committed prefix of `prev.content`
 * @param err           the error event payload
 * @param timestamp     fallback timestamp for a message that has none yet
 */
export function applyStreamError(
  prev: MessageType,
  committedLen: number,
  err: StreamErrorInfo,
  timestamp: string,
): MessageType {
  const committedText = prev.content.slice(0, Math.max(0, committedLen));

  if (!hasKeepableContent(prev, committedText)) {
    // Nothing worth keeping — the message becomes the error itself.
    return {
      ...prev,
      responseType: "error",
      content: err.message || "Something went wrong while processing your question.",
      error: {
        code: err.code,
        message: err.message,
        recoverable: err.recoverable,
      },
      streamError: undefined,
      timestamp: prev.timestamp ?? timestamp,
    };
  }

  return {
    ...prev,
    // Drop the provisional tail, like a "discard" boundary would have.
    content: committedText,
    streamError: err,
    timestamp: prev.timestamp ?? timestamp,
  };
}
